import React, { Suspense, useEffect, useState } from 'react';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { GROWTHBOOK_API_HOST } from 'common/config/constants';
import { CREO_TOPICS } from 'common/config/funnelsAndCourses';
import { analytics } from 'common/services/analytics/Analytics';
import { server } from 'common/services/server/server';
import { AbTestItem } from 'common/types/growthbook';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from 'common/utils/methods/handleLocalStorage';
import FullscreenLoader from './common/components/elements/FullscreenLoader/FullscreenLoader';
import { DEFAULT_QUIZ, QUIZ_IDS } from './common/config/funnels';
import { FLOWS_AB_TEST } from './common/config/growthbook';
import useRouteIds from './common/hooks/useRouteIds';
import useUrls from './common/hooks/useUrls';
import useUserIds from './common/hooks/useUserIds';
import NotFoundPage from './common/pages/StaticPages/NotFoundPage';
import FUNNEL_ROUTES, { STATIC_ROUTES } from './common/routes/routes';
import { STATIC_PAGES_PATHS } from './common/routes/types';
import loadFacebookPixelScript from './common/scripts/loadFacebookPixelScript';
import { PAGES, USER } from './common/types';
import saveFbGoogleData from './common/utils/analytics/saveFbGoogleData';

const {
  VITE_AMPLITUDE_API_KEY,
  VITE_GROWTHBOOK_API_KEY,
  VITE_FACEBOOK_PIXEL_API_KEY_PRIMARY,
} = import.meta.env;

analytics.init(VITE_AMPLITUDE_API_KEY, VITE_FACEBOOK_PIXEL_API_KEY_PRIMARY);

const timestamp = Date.now();
const tests: AbTestItem[] = [];
const storageDataChangedEvent = 'storageDataChanged';

const growthbook = new GrowthBook({
  apiHost: GROWTHBOOK_API_HOST,
  clientKey: VITE_GROWTHBOOK_API_KEY,
  enableDevMode: true,
  trackingCallback: (experiment, result) => {
    const abTestItemGb: AbTestItem = {
      name: experiment.key,
      value: result.value,
      timestamp,
    };

    tests.push(abTestItemGb);
    localStorage.setItem(USER.TESTS, JSON.stringify(tests));

    const dataChangedEvent = new CustomEvent(storageDataChangedEvent, {
      detail: tests,
    });
    window.dispatchEvent(dataChangedEvent);

    const abValuesAnalytics: Record<string, any> = {};
    abValuesAnalytics[experiment.key] = result.value;

    analytics.setUserProperties({
      'ab test': abValuesAnalytics,
    });

    analytics.send({
      eventName: 'experiment_started',
      data: {
        experiment_name: experiment.key,
        experiment_value: result.value,
      },
    });
  },
});

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [urlParams, setUrlParams] = useState('');
  const [pixelInitialized, setPixelInitialized] = useState(false);

  const { userId, quizId, userCountry } = useUserIds().ids;
  const { quizIdFromRoute, userIdFromRoute } = useRouteIds();

  const { setUserId, setQuizId, setUserCountry, setRecommendedCourseId } =
    useUserIds().actions;
  const { setPurchaseUrl } = useUrls().actions;

  const userIdFromLocalStorage = getLocalStorageItem<string>(USER.ID);
  const queryParams = new URLSearchParams(location.search);

  // Check if current path is an excluded static page
  const isExcludedStaticPage = [
    STATIC_PAGES_PATHS.SUBSCRIPTION_TERMS,
    STATIC_PAGES_PATHS.PRIVACY_POLICY,
    STATIC_PAGES_PATHS.TERMS_AND_CONDITIONS,
  ].some((path) => location.pathname.includes(path));

  useEffect(() => {
    if (isExcludedStaticPage) {
      return;
    }

    (async (): Promise<void> => {
      try {
        setIsLoading(true);

        const unvalidatedUserId =
          userIdFromRoute || userIdFromLocalStorage || '';

        const body = {
          ...(unvalidatedUserId && {
            id: unvalidatedUserId,
          }),
          isTestUser: queryParams.get('sandbox') === 'true',
        };

        const userFromServer = await server.createUserOrGetIfExist({
          ...body,
        });

        if (userFromServer) {
          setUserId(userFromServer.id);
          setUserCountry(userFromServer.country);
          setLocalStorageItem(USER.ID, userFromServer.id);
        }

        const unvalidateQuizId = quizIdFromRoute;

        if (Object.values(QUIZ_IDS).includes(unvalidateQuizId)) {
          setQuizId(quizIdFromRoute);
        } else {
          setQuizId(DEFAULT_QUIZ);
        }
      } catch (error) {
        console.error(error);
      }
    })();

    const utmParams = new URLSearchParams(window.location.search);

    const paramsObject: Record<string, string> = {};

    utmParams.forEach((value, key) => {
      if (key.startsWith('utm_') || key === 'gclid') {
        paramsObject[key] = value;
      }
    });

    const utmParamsString = new URLSearchParams(paramsObject).toString();
    const recommendedCourseParam = queryParams
      .get('utm_creo_topic')
      ?.toString();

    setUrlParams(utmParamsString);

    if (recommendedCourseParam && recommendedCourseParam in CREO_TOPICS) {
      setRecommendedCourseId(
        CREO_TOPICS[recommendedCourseParam as keyof typeof CREO_TOPICS],
      );
    }
  }, []);

  useEffect(() => {
    if (!userId) return;

    saveFbGoogleData(userId, queryParams);
  }, [userId, location.search]);

  useEffect(() => {
    if (!quizId || !userId) {
      return;
    }

    analytics.setUserId(userId);
    analytics.setUserViewPortAndOs();
    analytics.setUserUTMs(queryParams, quizId);

    growthbook.init({ streaming: true });
    growthbook.setAttributes({
      id: userId,
      funnel: quizId,
      country: userCountry,
    });

    const userPurchaseUrl = `${origin}/m/${quizId}/${userId}/${PAGES.THANK_YOU_PAGE}`;
    setPurchaseUrl(userPurchaseUrl);

    (async (): Promise<void> => {
      try {
        setIsLoading(true);

        analytics.trackPageView({
          eventName: 'PageView',
          data: { location },
          userId,
        });

        await server.savePageViewsByUserId(userId, {
          pageViews: [
            {
              timestamp: new Date().getTime(),
              url: urlParams
                ? `${location.pathname}?${urlParams}`
                : `${location.pathname}`,
            },
          ],
        });
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [userId, quizId]);

  useEffect(() => {
    const handleClick = async (): Promise<any> => {
      if (!pixelInitialized) {
        await loadFacebookPixelScript();
        const pagePath = window.location.pathname;

        if (pagePath.includes('pl-ff-1m') || pagePath.includes('pl-mt-cs')) {
          window.fbq('init', '533144466205071');
        } else {
          window.fbq('init', VITE_FACEBOOK_PIXEL_API_KEY_PRIMARY);
        }
        setPixelInitialized(true);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [pixelInitialized]);

  useEffect((): (() => void) | undefined => {
    const isABTestInCurrentFlow = FLOWS_AB_TEST[quizId];

    if (!isABTestInCurrentFlow) return;

    const getAndSaveDataFromStorage = (): void => {
      const testsFromStorage = localStorage.getItem(USER.TESTS);

      if (!testsFromStorage) return;

      const parsedTests = JSON.parse(testsFromStorage);

      if (parsedTests.length > 0) {
        saveAbTests(parsedTests);
      }
    };

    const saveAbTests = async (payload: AbTestItem[]): Promise<void> => {
      const abTestsWithUserId = payload.map((test) => ({
        ...test,
        userId: userId,
      }));

      try {
        await server.saveAbTests(abTestsWithUserId);
      } catch (e) {
        console.error(e);
      }
    };

    const eventHandler = (event: any): void => {
      const newData = event.detail;
      if (newData) {
        getAndSaveDataFromStorage();
      }
    };
    window.addEventListener(storageDataChangedEvent, eventHandler);
    return () =>
      window.removeEventListener(storageDataChangedEvent, eventHandler);
  }, []);

  return (
    <>
      {isExcludedStaticPage && (
        <Suspense fallback={<FullscreenLoader />}>
          <GrowthBookProvider growthbook={growthbook}>
            <Routes>
              {STATIC_ROUTES.map((route) => (
                <Route
                  path={route.path}
                  key={route.path}
                  element={<route.Component />}
                />
              ))}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </GrowthBookProvider>
        </Suspense>
      )}

      {!isExcludedStaticPage && (
        <>
          {(isLoading || !userId) && <FullscreenLoader />}
          {!isLoading && userId && (
            <GrowthBookProvider growthbook={growthbook}>
              <Suspense fallback={<FullscreenLoader />}>
                <Routes>
                  {FUNNEL_ROUTES.map((route) => (
                    <Route
                      path={`${route.path}/${userId}/*`}
                      key={route.path}
                      element={<route.Component />}
                    />
                  ))}

                  <Route
                    path="/"
                    element={<Navigate to={`/m/${DEFAULT_QUIZ}/${userId}`} />}
                  />
                  <Route
                    path="/m"
                    element={<Navigate to={`/m/${DEFAULT_QUIZ}/${userId}`} />}
                  />
                  <Route
                    path="/m/:quizId"
                    element={<Navigate to={`/m/${quizId}/${userId}`} />}
                  />
                  {STATIC_ROUTES.map((route) => (
                    <Route
                      path={route.path}
                      key={route.path}
                      element={<route.Component />}
                    />
                  ))}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </GrowthBookProvider>
          )}
        </>
      )}
    </>
  );
};

export default App;
